import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface Team {
  id: string;
  shop_id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useTeams() {
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();
  const shop = activeShop;

  const teamsQuery = useQuery({
    queryKey: ['teams', shop?.id],
    queryFn: async () => {
      if (!shop?.id) return [];
      
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('shop_id', shop.id)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      return data as Team[];
    },
    enabled: !!shop?.id,
  });

  const createTeam = useMutation({
    mutationFn: async (name: string) => {
      if (!shop?.id) throw new Error('No shop selected');

      const { data, error } = await supabase
        .from('teams')
        .insert({ shop_id: shop.id, name })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', shop?.id] });
      toast.success('Team created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create team: ${error.message}`);
    },
  });

  const updateTeam = useMutation({
    mutationFn: async ({ id, name, is_active }: { id: string; name?: string; is_active?: boolean }) => {
      const updates: Partial<Team> = {};
      if (name !== undefined) updates.name = name;
      if (is_active !== undefined) updates.is_active = is_active;

      const { data, error } = await supabase
        .from('teams')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', shop?.id] });
      toast.success('Team updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update team: ${error.message}`);
    },
  });

  const deleteTeam = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams', shop?.id] });
      toast.success('Team deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete team: ${error.message}`);
    },
  });

  return {
    teams: teamsQuery.data ?? [],
    isLoading: teamsQuery.isLoading,
    error: teamsQuery.error,
    createTeam,
    updateTeam,
    deleteTeam,
    refetch: teamsQuery.refetch,
  };
}
